import React, { useState, useEffect } from 'react';
import Button from '@/common/components/ui/Button';
import StandardInputFile from '@/common/components/input/StandardInputFile';
import AssignmentService, {
  AssignmentData,
  SubmissionData,
} from '@/services/course/assignment-service';
import UtilityService from '@/services/util/utility-service';
import { Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppDispatch } from '@/lib/hooks';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';
import DeleteSubmissionModal from '@/common/components/modals/custom/confirmation/DeleteSubmissionModal';
import { CourseId } from 'course-types';

interface Props {
  assignment: AssignmentData;
  submission: SubmissionData | null;
  setSubmission: React.Dispatch<React.SetStateAction<SubmissionData | null>>;
  courseId: CourseId;
  assignmentFileBlob: Blob | null;
}

const AssignmentControls = ({
  assignment,
  submission,
  setSubmission,
  courseId,
  assignmentFileBlob,
}: Props) => {
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [submissionFileBlob, setSubmissionFileBlob] = useState<Blob | null>(null);
  const [_, setSubmissionFileMimeType] = useState<string>('');

  const formatDate = (dueDate: Date | null | undefined) => {
    if (!dueDate) return null;
    return UtilityService.formatDate(new Date(dueDate).toISOString(), true);
  };

  useEffect(() => {
    AssignmentService.initializeAssignmentOpen(
      assignment.id,
      courseId,
      assignment.parent_unit,
      assignment.parent_subunit,
    );
  }, []);

  // Fetch submission file blob if submission exists
  useEffect(() => {
    const fetchSubmissionFileBlob = async () => {
      if (!submission || submissionFileBlob) return;

      const result = await AssignmentService.fetchSubmissionFile(assignment.id);

      if (result) {
        setSubmissionFileBlob(result.blob);
        setSubmissionFileMimeType(result.mimeType);
      }
    };

    fetchSubmissionFileBlob();
  }, [submission, assignment.id, submissionFileBlob]);

  const handleDownloadAssignment = () => {
    if (!assignmentFileBlob || !assignment) return;
    UtilityService.downloadBlob(assignmentFileBlob, assignment.name || 'assignment-file');
  };

  const handleRemoveFile = () => {
    setUploadFile(null);
  };

  const handleSubmitAssignment = async () => {
    if (!uploadFile) {
      toast.error('Please select a file to submit');
      return;
    }

    try {
      setIsSubmitting(true);

      const result = await AssignmentService.submitAssignment(
        assignment.id,
        uploadFile,
        courseId,
        assignment.parent_unit,
        assignment.parent_subunit,
      );

      if (result.error) {
        return;
      }

      setSubmission({
        submissionDate: result.submission?.submissionDate
          ? new Date(result.submission.submissionDate)
          : new Date(),
      });

      const submissionFileResult = await AssignmentService.fetchSubmissionFile(assignment.id);
      if (submissionFileResult) {
        setSubmissionFileBlob(submissionFileResult.blob);
        setSubmissionFileMimeType(submissionFileResult.mimeType);
      }

      setUploadFile(null);
    } catch (error) {
      console.error('Error submitting assignment:', error);
      toast.error('Failed to submit assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (files: FileList | null) => {
    if (files && files.length > 0) {
      setUploadFile(files[0]);
    }
  };

  const handleDownloadSubmission = async () => {
    if (!submission) return;
    await AssignmentService.downloadSubmission(assignment.id, assignment.name);
  };

  const handleDeleteSubmission = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(setModalName('DeleteSubmission'));
    dispatch(setModalVisible(true));
  };

  const isSubmitted = !!submission;

  // Check if submission can be deleted
  const canDeleteSubmission =
    submission &&
    (!assignment.due_date || new Date() <= new Date(assignment.due_date)) &&
    (submission.grade === undefined || submission.grade === null);

  return (
    <div className='w-96 min-w-96 space-y-6'>
      {/* Due Date */}
      {assignment.due_date && (
        <div className='bg-white border border-border_gray_300 rounded-lg p-6'>
          <div className='bg-surface_gray_100 rounded-lg p-3'>
            <p className='text-sm font-medium text-text_gray_700'>Due Date:</p>
            <p className='text-base text-text_gray_900 font-medium'>
              {formatDate(assignment.due_date)}
            </p>
          </div>
        </div>
      )}

      {/* Download Section */}
      {assignmentFileBlob && (
        <div className='bg-white border border-border_gray_300 rounded-lg p-6'>
          <h3 className='text-lg font-semibold text-text_gray_900 mb-4'>Assignment Materials</h3>
          <Button
            onClick={handleDownloadAssignment}
            className='w-full bg-blue-600 text-white rounded-lg px-4 py-2 flex items-center justify-center gap-2 hover:bg-blue-700'
          >
            Download Assignment File
          </Button>
        </div>
      )}

      {/* User Submission Section - Show if submitted */}
      {isSubmitted && submission && (
        <div className='bg-white border border-border_gray_300 rounded-lg p-6'>
          <h3 className='text-lg font-semibold text-text_gray_900 mb-4'>Your Submission</h3>

          <div className='flex flex-col gap-2'>
            {submission?.grade !== undefined && submission?.grade !== null && (
              <div className='bg-surface_gray_100 border border-border_gray_300 rounded-lg p-3'>
                <p className='text-sm font-medium text-text_gray_700'>
                  Grade:{' '}
                  <span className='text-text_gray_900 font-semibold'>{submission.grade}%</span>
                </p>
              </div>
            )}

            <div className='space-y-4'>
              <div className='bg-green-50 border border-green-200 rounded-lg p-3'>
                <p className='text-sm font-medium text-green-800'>
                  Submitted:{' '}
                  <span className='text-green-900'>{formatDate(submission.submissionDate)}</span>
                </p>
              </div>
            </div>

            <div className='flex gap-2'>
              {submissionFileBlob && (
                <Button
                  onClick={handleDownloadSubmission}
                  className='flex-1 bg-blue-600 text-white rounded-lg px-4 py-2 hover:bg-blue-700'
                >
                  Download Submission
                </Button>
              )}

              {canDeleteSubmission && (
                <Button onClick={handleDeleteSubmission} delete>
                  <Trash2 size={18} />
                  Delete
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Submission Section - Only show if not submitted */}
      {!isSubmitted && (
        <div className='bg-white border border-border_gray_300 rounded-lg p-6'>
          <h3 className='text-lg font-semibold text-text_gray_900 mb-4'>Submit Assignment</h3>

          <div className='space-y-4'>
            <p className='text-text_gray_700 text-sm'>
              Upload your completed assignment file below.
            </p>

            <StandardInputFile
              id='assignmentSubmission'
              label='Select file to submit'
              accept='.pdf,.doc,.docx'
              onChange={(event) => handleFileChange(event.target.files)}
              isRequired={true}
            />

            {uploadFile && (
              <div className='bg-surface_gray_100 rounded-lg p-3 flex items-center justify-between'>
                <p className='text-sm text-text_gray_700'>
                  Selected: <span className='font-medium'>{uploadFile.name}</span>
                </p>
                <button
                  onClick={handleRemoveFile}
                  className='text-red-600 hover:text-red-700 p-1'
                  title='Remove file'
                >
                  <Trash2 size={18} />
                </button>
              </div>
            )}

            <Button
              onClick={handleSubmitAssignment}
              disabled={!uploadFile || isSubmitting}
              className={`w-full rounded-lg px-6 py-3 flex items-center justify-center gap-2`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Assignment'}
            </Button>
          </div>
        </div>
      )}

      {/* Delete Submission Modal */}
      <DeleteSubmissionModal
        assignment={assignment}
        onDeleteSuccess={() => {
          setSubmission(null);
          setSubmissionFileBlob(null);
          setSubmissionFileMimeType('');
        }}
        courseId={courseId}
      />
    </div>
  );
};

export default AssignmentControls;
